import type { Vector3 } from "@pax/basemap";
import { cross, dot, GlobeCamera, normalize, rotate, zoomShowing } from "./globe";
import {
  type FlatCamera,
  flatAt,
  flatFit,
  flatLookingAt,
  flatPanned,
  flatZoomed,
} from "./projection";

/**
 * How a map is looked at and moved: the camera, every gesture on it, and the motion that carries on
 * after one, as the Earth budget spike's viewer does it. Every gesture on the globe is one rule, "the
 * ground you grabbed stays under the pointer". Letting go of a drag leaves the map moving as it moved
 * over the pointer's last stretch, slowed by friction; on the globe that is a spin about the axis it
 * was turning on. While the globe's poles sweep across the view fast, as a fling makes them, the
 * world's axis shows through them.
 */

export type MapKind = "flat" | "globe";

export interface Size {
  width: number;
  height: number;
}

/** What the renderer draws from. The globe's camera is read as it is at the moment of drawing. */
export type DrawCamera = FlatCamera | { kind: "globe"; camera: GlobeCamera };

/** Where a map is looking, to hand to another view of the same world. */
export interface Look {
  kind: MapKind;
  flat: FlatCamera;
  globe: { target: Vector3; up: Vector3; zoom: number };
  size: Size;
}

/**
 * Where a map looks, said the same way whatever the frame's size, for an address: the kind of map,
 * the place at its middle in degrees, and how many doublings it's zoomed past the whole world.
 */
export interface Place {
  kind: MapKind;
  latitude: number;
  longitude: number;
  zoom: number;
}

/** Seconds for coasting motion to fall by a factor of e. */
const FRICTION_SECONDS = 0.3;
/** The pointer's last stretch of motion that sets the motion on release. */
const RELEASE_WINDOW_MS = 80;
/** Seconds for a button's zoom to close most (1 − 1/e) of its distance. */
const ZOOM_EASE_SECONDS = 0.08;
/** The share of the frame's shorter side the whole globe takes, and the least it can. */
const GLOBE_FIT = 0.86;
const GLOBE_LEAST = 0.3;
/** The most screen pixels a radian of the world can span: about 100 m a pixel on Earth. */
const MOST_PIXELS_PER_RADIAN = 60_000;
/** Pole sweep, radians a second in the camera's frame, where the axis starts to show and shows fully. */
const AXIS_FROM = 0.5;
const AXIS_FULLY = 1.5;
/** Seconds for the axis to come in, and to fade once the poles slow (each to 1 − 1/e of the way). */
const AXIS_IN_SECONDS = 0.08;
const AXIS_OUT_SECONDS = 0.6;
/** Where the map opens: a little east of 0° and north of the equator. */
const START = lonLatPoint(10, 20);

type Press =
  | { kind: "flat"; at: [number, number] }
  | { kind: "globe"; ground: Vector3; at: [number, number] }
  | {
      kind: "pinch";
      ground: Vector3 | null;
      distance: number;
      zoom: number;
      flat: FlatCamera;
      middle: [number, number];
    };

export class MapView {
  kind: MapKind;
  private size: Size = { width: 0, height: 0 };
  private flat: FlatCamera = { kind: "flat", x: 0, y: 0, scale: 1 };
  private globe = new GlobeCamera(START);
  private press: Press | null = null;
  private samples: { time: number; x: number; y: number; target: Vector3 }[] = [];
  private coast: { vx: number; vy: number } | null = null;
  private spin: { axis: Vector3; speed: number } | null = null;
  private zooming: { to: number; at: [number, number]; ground: Vector3 | null } | null = null;
  /** The poles in the camera's frame last frame, and how much of the axis shows. */
  private poles: Vector3[] | null = null;
  axis = 0;
  private request = 0;
  private previous = 0;
  private pending: Look | null;
  private startAt: Place | null;

  /**
   * `changed` is called whenever the map should be drawn again. It starts where `look` was, or at
   * `place` once it knows its size, or showing the whole world.
   */
  constructor(
    private readonly changed: () => void,
    look?: Look | null,
    place?: Place | null,
  ) {
    this.kind = look?.kind ?? place?.kind ?? "globe";
    this.pending = look ?? null;
    this.startAt = look ? null : (place ?? null);
  }

  /** Takes the frame's new size, keeping where the map looks and how far past the whole world. */
  resize(size: Size) {
    if (!size.width || !size.height) return;
    const look = this.pending;
    this.pending = null;
    if (look?.size.width) {
      this.flat = look.flat;
      this.globe = new GlobeCamera(look.globe.target, look.globe.up);
      this.globe.zoom = look.globe.zoom;
      this.setSize(look.size);
    } else if (!this.size.width) {
      this.setSize(size);
      this.fit();
      if (this.startAt) this.goTo(this.startAt);
      this.startAt = null;
      return;
    }
    const before = this.size;
    const zoomPast = this.globe.zoom - zoomShowing(GLOBE_FIT, before.width, before.height);
    const flatPast = this.flat.scale / flatFit(before);
    this.setSize(size);
    this.globe.zoom = zoomShowing(GLOBE_FIT, size.width, size.height) + zoomPast;
    this.globe.update();
    this.flat = flatLookingAt(this.flat, flatFit(size) * flatPast, size);
    this.changed();
  }

  private setSize(size: Size) {
    this.size = size;
    this.globe.width = size.width;
    this.globe.height = size.height;
    this.globe.update();
  }

  /** The whole world, north up. */
  fit() {
    this.stop();
    this.flat = { kind: "flat", x: 0, y: 0, scale: flatFit(this.size) };
    this.globe = new GlobeCamera(this.globe.target);
    this.globe.zoom = zoomShowing(GLOBE_FIT, this.size.width, this.size.height);
    this.setSize(this.size);
    this.changed();
  }

  /** The other view, looking at the same place at the same scale, or the whole world if nearer. */
  setKind(kind: MapKind) {
    if (kind === this.kind) return;
    this.stop();
    const center = this.pointAt(this.size.width / 2, this.size.height / 2) ?? this.globe.target;
    if (kind === "globe") {
      const scale = this.flat.scale;
      this.globe = new GlobeCamera(center);
      this.setSize(this.size);
      this.globe.zoom = Math.max(this.fitZoom(), this.zoomFor(scale));
      this.globe.update();
    } else {
      this.flat = flatLookingAt(
        { kind: "flat", ...lonLatOf(center), scale: 0 },
        Math.max(flatFit(this.size), this.globe.pixelsPerRadian),
        this.size,
      );
    }
    this.kind = kind;
    this.changed();
  }

  /** Where the map looks, for an address. */
  place(): Place {
    const center = this.pointAt(this.size.width / 2, this.size.height / 2) ?? this.globe.target;
    return {
      kind: this.kind,
      latitude: (Math.asin(Math.max(-1, Math.min(1, center[2]))) * 180) / Math.PI,
      longitude: (Math.atan2(center[1], center[0]) * 180) / Math.PI,
      zoom: Math.log2(this.zoomRatio),
    };
  }

  /** Looks at a place, north up. */
  goTo(place: Place) {
    this.stop();
    this.kind = place.kind;
    const ratio = 2 ** Math.max(0, place.zoom);
    if (place.kind === "flat") {
      this.flat = flatLookingAt(
        {
          kind: "flat",
          x: (place.longitude * Math.PI) / 180,
          y: (place.latitude * Math.PI) / 180,
          scale: 0,
        },
        Math.min(MOST_PIXELS_PER_RADIAN, flatFit(this.size) * ratio),
        this.size,
      );
    } else {
      this.globe = new GlobeCamera(lonLatPoint(place.longitude, place.latitude));
      this.setSize(this.size);
      this.globe.zoom = this.clampZoom(this.fitZoom() + Math.log2(ratio));
      this.globe.update();
    }
    this.changed();
  }

  camera(): DrawCamera {
    return this.kind === "flat" ? this.flat : { kind: "globe", camera: this.globe };
  }

  look(): Look {
    return {
      kind: this.kind,
      flat: this.flat,
      globe: { target: this.globe.target, up: this.globe.up, zoom: this.globe.zoom },
      size: this.size,
    };
  }

  /** The world direction under a screen point, or null off the world. */
  pointAt(x: number, y: number): Vector3 | null {
    return this.kind === "flat" ? flatAt(this.flat, this.size, x, y) : this.globe.pick(x, y);
  }

  /**
   * Where world points are drawn on screen, in order, or null for one out of sight behind the globe.
   * On the flat map each point is taken the way round the world nearest the one before, so a line
   * crossing the date line stays whole.
   */
  project(points: Vector3[]): ([number, number] | null)[] {
    const { width, height } = this.size;
    if (this.kind === "flat") {
      const { x, y, scale } = this.flat;
      let previous = x;
      return points.map(point => {
        const { x: longitude, y: latitude } = lonLatOf(point);
        const turns = Math.round((previous - longitude) / (2 * Math.PI));
        previous = longitude + turns * 2 * Math.PI;
        return [width / 2 + (previous - x) * scale, height / 2 - (latitude - y) * scale];
      });
    }
    const globe = this.globe;
    const near = Math.max(1e-7, 2 / globe.pixelsPerRadian);
    return points.map(point => {
      const screen = globe.screenOf(point, globe.target);
      const back = globe.pick(...screen);
      return back && angleBetween(back, point) < near ? screen : null;
    });
  }

  /** Screen pixels a radian of the world spans at the middle of the screen. */
  get pixelsPerRadian(): number {
    return this.kind === "flat" ? this.flat.scale : this.globe.pixelsPerRadian;
  }

  /** How far in the map is past showing the whole world: 1 when it shows it all. */
  get zoomRatio(): number {
    return this.kind === "flat"
      ? this.flat.scale / flatFit(this.size)
      : 2 ** (this.globe.zoom - this.fitZoom());
  }

  /** Zooms by `factor` about a screen point, the middle unless given; `eased` glides there. */
  zoomBy(factor: number, at?: [number, number], eased = false) {
    const point = at ?? [this.size.width / 2, this.size.height / 2];
    this.coast = null;
    this.spin = null;
    if (this.kind === "flat") {
      this.flat = flatZoomed(this.flat, this.size, factor, point[0], point[1], this.flatLimits());
      this.changed();
      return;
    }
    const from = this.zooming?.to ?? this.globe.zoom;
    const to = this.clampZoom(from + Math.log2(factor));
    if (eased) {
      this.zooming = { to, at: point, ground: this.globe.pickNearest(...point) };
      this.wake();
      return;
    }
    this.zooming = null;
    this.globe.zoomAt(to, ...point);
    this.wake();
    this.changed();
  }

  /** A pointer pressed on the map: the ground under it is grabbed. */
  pressStart(x: number, y: number) {
    this.stop();
    this.samples = [];
    this.press =
      this.kind === "flat"
        ? { kind: "flat", at: [x, y] }
        : { kind: "globe", ground: this.globe.pickNearest(x, y), at: [x, y] };
    this.record(x, y);
    this.wake();
  }

  pressMove(x: number, y: number) {
    const press = this.press;
    if (!press || press.kind === "pinch") return;
    if (press.kind === "flat") {
      this.flat = flatPanned(this.flat, this.size, x - press.at[0], y - press.at[1]);
    } else {
      this.globe.grab(press.ground, x, y);
    }
    press.at = [x, y];
    this.record(x, y);
    this.changed();
  }

  /** The pointer let go: the map carries on as it moved over the pointer's last stretch. */
  pressEnd() {
    const press = this.press;
    this.press = null;
    if (!press || press.kind === "pinch") return;
    const now = performance.now();
    this.record(...press.at, now);
    const recent = this.samples.filter(sample => now - sample.time <= RELEASE_WINDOW_MS);
    this.samples = [];
    const first = recent[0];
    const last = recent.at(-1);
    const seconds = first && last ? (last.time - first.time) / 1000 : 0;
    if (!first || !last || seconds <= 0) return;
    if (press.kind === "flat") {
      this.coast = { vx: (last.x - first.x) / seconds, vy: (last.y - first.y) / seconds };
    } else {
      const turned = angleBetween(first.target, last.target);
      if (turned > 0) {
        this.spin = {
          axis: normalize(cross(first.target, last.target)),
          speed: turned / seconds,
        };
      }
    }
    this.wake();
  }

  /** Two pointers down: the ground between them is held between them as they spread or close. */
  pinchStart(a: [number, number], b: [number, number]) {
    this.stop();
    const middle: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    this.press = {
      kind: "pinch",
      ground: this.kind === "globe" ? this.globe.pickNearest(...middle) : null,
      distance: Math.max(1, Math.hypot(a[0] - b[0], a[1] - b[1])),
      zoom: this.globe.zoom,
      flat: this.flat,
      middle,
    };
  }

  pinchMove(a: [number, number], b: [number, number]) {
    const press = this.press;
    if (press?.kind !== "pinch") return;
    const middle: [number, number] = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const factor = Math.max(1, Math.hypot(a[0] - b[0], a[1] - b[1])) / press.distance;
    if (this.kind === "flat") {
      const zoomed = flatZoomed(
        press.flat,
        this.size,
        factor,
        press.middle[0],
        press.middle[1],
        this.flatLimits(),
      );
      this.flat = flatPanned(
        zoomed,
        this.size,
        middle[0] - press.middle[0],
        middle[1] - press.middle[1],
      );
    } else if (press.ground) {
      this.globe.zoom = this.clampZoom(press.zoom + Math.log2(factor));
      this.globe.update();
      this.globe.grab(press.ground, ...middle);
    }
    this.changed();
  }

  pinchEnd() {
    if (this.press?.kind === "pinch") this.press = null;
  }

  /** Stops any motion where it is. */
  stop() {
    this.coast = null;
    this.spin = null;
    this.zooming = null;
  }

  dispose() {
    cancelAnimationFrame(this.request);
    this.request = 0;
  }

  private record(x: number, y: number, time = performance.now()) {
    this.samples.push({ time, x, y, target: this.globe.target });
    while (this.samples.length > 2 && time - (this.samples[1]?.time ?? time) > RELEASE_WINDOW_MS) {
      this.samples.shift();
    }
  }

  private fitZoom() {
    return zoomShowing(GLOBE_FIT, this.size.width, this.size.height);
  }

  /** The globe zoom at which a radian spans `pixels` straight below. */
  private zoomFor(pixels: number) {
    const probe = this.globe.clone();
    probe.zoom = 0;
    probe.update();
    return Math.log2(pixels / probe.pixelsPerRadian);
  }

  private clampZoom(zoom: number) {
    const least = zoomShowing(GLOBE_LEAST, this.size.width, this.size.height);
    return Math.min(this.zoomFor(MOST_PIXELS_PER_RADIAN), Math.max(least, zoom));
  }

  private flatLimits() {
    return { least: flatFit(this.size), most: MOST_PIXELS_PER_RADIAN };
  }

  /** Runs the frame loop while anything moves on its own. */
  private wake() {
    if (this.request) return;
    this.previous = performance.now();
    this.request = requestAnimationFrame(this.frame);
  }

  private frame = (now: number) => {
    this.request = 0;
    const dt = Math.min(0.05, (now - this.previous) / 1000);
    this.previous = now;
    let moved = false;

    if (this.coast && this.kind === "flat") {
      const { vx, vy } = this.coast;
      if (Math.hypot(vx, vy) * dt < 0.5 && dt > 0) this.coast = null;
      else {
        this.flat = flatPanned(this.flat, this.size, vx * dt, vy * dt);
        const decay = Math.exp(-dt / FRICTION_SECONDS);
        this.coast = { vx: vx * decay, vy: vy * decay };
        moved = true;
      }
    }

    if (this.kind === "globe") {
      const globe = this.globe;
      if (this.spin && !this.press) {
        globe.moveTo(rotate(globe.target, this.spin.axis, this.spin.speed * dt));
        this.spin.speed *= Math.exp(-dt / FRICTION_SECONDS);
        // Stops once the globe would move less than half a pixel a frame.
        if (this.spin.speed * dt < 0.5 / globe.pixelsPerRadian) this.spin = null;
        moved = true;
      }
      if (this.zooming) {
        const left = this.zooming.to - globe.zoom;
        globe.zoom =
          Math.abs(left) < 1e-3
            ? this.zooming.to
            : globe.zoom + left * (1 - Math.exp(-dt / ZOOM_EASE_SECONDS));
        globe.update();
        if (this.zooming.ground) globe.grab(this.zooming.ground, ...this.zooming.at);
        if (globe.zoom === this.zooming.to) this.zooming = null;
        moved = true;
      }
      // The compass rights the heading; a held grab is solved again so the ground stays put.
      if (Math.abs(globe.headingError()) > 1e-4) {
        globe.compass(dt);
        const press = this.press;
        if (press?.kind === "globe") globe.grab(press.ground, ...press.at);
        else if (press?.kind === "pinch" && press.ground) globe.grab(press.ground, ...press.middle);
        moved = true;
      }
    }

    const axisBefore = this.axis;
    this.followPoles(dt);
    if (this.axis !== axisBefore) moved = true;

    if (moved) this.changed();
    const busy =
      this.press !== null ||
      this.coast !== null ||
      this.spin !== null ||
      this.zooming !== null ||
      this.axis > 0.01 ||
      (this.kind === "globe" && Math.abs(this.globe.headingError()) > 1e-4);
    if (busy) this.request = requestAnimationFrame(this.frame);
    else this.poles = null;
  };

  /** How fast the poles sweep in the camera's own frame, so zooming, which doesn't turn it, never shows the axis. */
  private followPoles(dt: number) {
    if (this.kind !== "globe") {
      this.axis = 0;
      this.poles = null;
      return;
    }
    const { east, north, target } = this.globe;
    const now: Vector3[] = [
      [east[2], north[2], target[2]],
      [-east[2], -north[2], -target[2]],
    ];
    const seen = this.poles;
    const sweep =
      seen && dt > 0
        ? Math.max(
            ...now.map((pole, index) => {
              const before = seen[index] ?? pole;
              return Math.hypot(pole[0] - before[0], pole[1] - before[1], pole[2] - before[2]);
            }),
          ) / dt
        : 0;
    this.poles = now;
    const wanted = smoothstep(AXIS_FROM, AXIS_FULLY, sweep);
    const seconds = wanted > this.axis ? AXIS_IN_SECONDS : AXIS_OUT_SECONDS;
    this.axis += (wanted - this.axis) * (1 - Math.exp(-dt / seconds));
    if (this.axis < 0.01 && wanted === 0) this.axis = 0;
  }
}

/** The globe's camera looking down at a place, the whole globe in a frame: for pictures of a world. */
export function globeLookingAt(
  longitude: number,
  latitude: number,
  size: Size,
  share = GLOBE_FIT,
): DrawCamera {
  const camera = new GlobeCamera(lonLatPoint(longitude, latitude));
  camera.width = size.width;
  camera.height = size.height;
  camera.zoom = zoomShowing(share, size.width, size.height);
  camera.update();
  return { kind: "globe", camera };
}

function lonLatPoint(longitude: number, latitude: number): Vector3 {
  const lon = (longitude * Math.PI) / 180;
  const lat = (latitude * Math.PI) / 180;
  return [Math.cos(lat) * Math.cos(lon), Math.cos(lat) * Math.sin(lon), Math.sin(lat)];
}

function lonLatOf(point: Vector3): { x: number; y: number } {
  return { x: Math.atan2(point[1], point[0]), y: Math.asin(Math.max(-1, Math.min(1, point[2]))) };
}

const angleBetween = (a: Vector3, b: Vector3) => Math.atan2(Math.hypot(...cross(a, b)), dot(a, b));

function smoothstep(from: number, to: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - from) / (to - from)));
  return t * t * (3 - 2 * t);
}
